import { UserSuppliersData } from '../../models/auth/usersuppliersdata';
import { UserData } from '../../models/auth/userdata';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { AppSettings } from '../../app.settings';
import { UserPermissions } from '../../permissions';
import { Router } from '@angular/router';
import { HttpClient } from '../../utils/http-client';
import { StorageSettings } from '../../utils/storage.settings';

import { Cookie } from '../../utils/cookies';
import { AppGlobalDataService } from 'src/app/services/appglobaldata.service';
import { AppGlobalDataProperties } from 'src/app/core/appglobaldata';
import { UISupplierContext } from 'src/app/models/internal/uisuppliercontext';
import { LocalstorageService } from 'src/app/services/localstorage.service';
import { Constants } from 'src/app/core/constants';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private headers: HttpHeaders;
    private storage: any;

    private auth_nonce = 'authNonce';
    private auth_state = 'authStateControl';
    private auth_validate_error = 'authError';
    private auth_validate_errordesc = 'authErrorDescription';

    private access_token = 'access_token';
    private user_email = 'ssEmail';
    private user_username = 'X-PortalIdentity-UserName';
    private user_currentSupplierCode = 'CurrentSupplierCode';
    private user_displayname = 'ssDisplayname';
    // private user_supplierCode: string = 'CurrentSupplierCode';
    private user_typeid = 'ssUserTypeid';
    private user_CSRFToken = 'ssCSRFToken';
    private user_SupplierName = 'ssUserSupplierName';
    private user_SupplierSiteCode = 'ssUserSupplierSiteCode';
    private user_SubcontractSuppliersLookup =
        'ssUserSubcontractSuppliersLookup';
    private user_IsSupplierEdiEnabled = 'IsSupplierEdiEnabled';
    private user_SuppliersData = 'SuppliersData';
    private user_isPlanner = 'IsPlanner';
    private user_isEmployee = 'IsEmployee';
    private user_isAdmin = 'IsAdmin';
    private user_isSupplier = 'IsSupplier';
    private user_Permissions = 'Permissions';
    locStorage = Constants.localStorageKeys;

    /**
     * Constructor
     * Sets the default headers and storage for subsequent requests.
     *
     * @param {Router} _router
     * @param {Http} _http
     * @param {HttpClient} _apiHttpClient
     * @param {AppSettings} _configuration
     * @param {StorageSettings} _sessionSettings
     */
    constructor(
        private _router: Router,
        private _apiHttpClient: HttpClient,
        private appSettings: AppSettings,
        private _sessionSettings: StorageSettings,
        private localstorageService: LocalstorageService,
        private appGlobalDataService: AppGlobalDataService
    ) {
        this.headers = new HttpHeaders();
        this.headers.append('Content-Type', 'application/json');
        this.headers.append('Accept', 'application/json');
        this.storage = localStorage;
    }

    /**
     * Performs the auth client configuration and
     * redirects to the Identity Server/Warp Login.
     *
     * This requires ClientID to be configured in the Identity Server and have an available Callback Url to parse the token.
     */
    public RequestAuthentication() {
        this.ResetAuthorizationData();

        const authorizationUrl = this.appSettings.OpenId_AuthorizationUrl;
        const client_id = this.appSettings.OpenId_ClientID;
        const redirect_uri = this.appSettings.OpenId_RedirectUrl;
        const response_type = 'id_token token';
        const scope = 'email openid profile groups';
        const nonce = 'N' + Math.random() + '' + Date.now();
        const state = Date.now() + '' + Math.random();

        this.store(this.auth_state, state);
        this.store(this.auth_nonce, nonce);

        this.deleteFromStorage(this.auth_validate_error);
        this.deleteFromStorage(this.auth_validate_errordesc);

        const url =
            authorizationUrl +
            '?' +
            'response_type=' +
            encodeURI(response_type) +
            '&' +
            'client_id=' +
            encodeURI(client_id) +
            '&' +
            'redirect_uri=' +
            encodeURI(redirect_uri) +
            '&' +
            'scope=' +
            encodeURI(scope) +
            '&' +
            'nonce=' +
            encodeURI(nonce) +
            '&' +
            'state=' +
            encodeURI(state);

        window.location.href = url;
    }

    /**
     * Processes the hashed/encrypted token from the Identity Server.
     * It validates the authenticity based on the nonce and stores the result in the localStorage.
     *
     * Upon success, it will redirect to the default route /. Otherwise it will redirect to the Unauthorized page.
     */
    public RequestAuthenticationCallback() {
        this.ResetAuthorizationData();

        const hash = window.location.hash.substr(1);

        const result: any = hash.split('&').reduce(function(result1, item) {
            const parts = item.split('=');
            result1[parts[0]] = parts[1];
            return result1;
        }, {});

        let token = '';
        let id_token = '';
        let authResponseIsValid = false;
        if (!result.error) {
            if (result.state !== this.retrieve(this.auth_state)) {
                this.store(this.auth_validate_error, 'openid_state_mismatch');
                this.store(
                    this.auth_validate_errordesc,
                    'The response state could not be validated.'
                );
            } else {
                token = result.access_token;
                id_token = result.id_token;

                const dataIdToken: any = this.parseDataFromToken(id_token);

                if (dataIdToken.nonce !== this.retrieve(this.auth_nonce)) {
                    this.store(
                        this.auth_validate_error,
                        'openid_nonce_mismatch'
                    );
                    this.store(
                        this.auth_validate_errordesc,
                        'The response nonce could not be validated.'
                    );
                } else {
                    // Validate result data.
                    this.deleteFromStorage(this.auth_nonce);
                    this.deleteFromStorage(this.auth_state);

                    // this.store(this.token_name, token);
                    this.store(
                        this.user_username,
                        dataIdToken.preferred_username ||
                            dataIdToken.Email ||
                            dataIdToken.email
                    );
                    this.store(
                        this.user_email,
                        dataIdToken.Email || dataIdToken.email
                    );
                    this.store(
                        this.user_displayname,
                        dataIdToken.Name || dataIdToken.name
                    );
                    this.appGlobalDataService.RefreshUserDisplayName(
                        this.retrieve(this.user_displayname)
                    );
                    this.store(this.access_token, token);

                    authResponseIsValid = true;
                }
            }
        } else {
            this.store(this.auth_validate_error, result.error);
            this.store(
                this.auth_validate_errordesc,
                decodeURI(result.error_description).replace(/\+/gi, ' ')
            );
        }

        if (authResponseIsValid) {
            this.fetchUserInformation();
        } else {
            this.ResetAuthorizationData();
            this._router.navigate(['/auth/unauthorized']);
        }
    }

    /**
     * Fetches the current information.
     */
    private fetchUserInformation() {
        const locStorage = this.locStorage;
        const me = this;
        this._apiHttpClient
            .getForType<UserData>(`${this.appSettings.Packaging2APIUrl}/user/me`)
            .toPromise()
            .then(response => {
                const ret = response.body;
                const data = ret.User;
                const warpSupplierCode = data.WarpSupplierCode;
                const warpUserTypeId = data.UserTypeId;
                let supplierName = '';
                let supplierSiteCode = '';
                const IsSupplierEdiEnabled = '';
                const supplierData = ret.supplierData;
                const listOfSupplierCodes: string[] = [];
                const IsPlanner = data.IsPlanner;
                const IsEmployee = data.IsEmployee;
                const IsAdmin = data.IsAdmin;
                const IsSupplier = data.IsSupplier;
                const UISupplierContexts: UISupplierContext[] = [];

                me.store(me.user_currentSupplierCode, warpSupplierCode);
                me.store(me.user_typeid, warpUserTypeId);
                me.store(me.user_CSRFToken, ret.formToken);
                me.store(me.user_isPlanner, IsPlanner);

                const permissions = {};
                (data.Permissions || []).forEach((p: string) => {
                    if (!permissions[p]) {
                        permissions[p] = true;
                    }
                });
                me.store(me.user_Permissions, permissions);

                if (IsEmployee) {
                    me.store(me.user_isEmployee, Constants.IsEmployeeCheckCode);
                } else {
                    me.store(me.user_isEmployee, '0');
                }
                if (IsAdmin) {
                    me.store(me.user_isAdmin, Constants.IsAdminCheckCode);
                } else {
                    me.store(me.user_isAdmin, '0');
                }
                this.appGlobalDataService.RefreshIsEmployee(me.retrieve(me.user_isEmployee));
                me.store(me.user_isSupplier, IsSupplier);
                this.appGlobalDataService.RefreshIsSupplier(IsSupplier);

                if (supplierData && supplierData.length > 1) {
                    me.store(me.user_SuppliersData, supplierData);
                    supplierData.forEach(element => {
                        listOfSupplierCodes.push(element.SupplierCode);
                    });
                }
                if (supplierData && supplierData.length > 0) {
                    supplierData.forEach(supData => {
                        const sup = new UISupplierContext();
                        sup.Name = supData.Name;
                        sup.SupplierCode = supData.SupplierCode;
                        sup.SupplierId = supData.SupplierId;
                        UISupplierContexts.push(sup);
                    });
                    // debugger;
                    this.appGlobalDataService.RefreshUISupplierContexts(
                        UISupplierContexts
                    );
                    this.localstorageService.store(locStorage.UISupplierContexts, UISupplierContexts);
                }
                me._sessionSettings.reloadSettings();

                // Fetching supplier Name
                if (
                    data.WarpSupplierCode !== undefined &&
                    data.WarpSupplierCode != null &&
                    data.WarpSupplierCode !== ''
                ) {
                    // me.store(me.user_SuppliersData, x);
                    const tempData: UserSuppliersData = supplierData.filter(
                        x => x.SupplierCode === data.WarpSupplierCode
                    );
                    if (tempData != null && tempData[0] != null) {
                        supplierName = tempData[0].Name;
                        supplierSiteCode = tempData[0].SerializationCode;
                        me.store(me.user_SupplierName, tempData[0].Name);
                        me.store(
                            me.user_SupplierSiteCode,
                            tempData[0].SerializationCode
                        );
                        me.store(
                            me.user_IsSupplierEdiEnabled,
                            tempData[0].IsAsnEdiEnabled
                        );
                        me._sessionSettings.reloadSettings();
                    }
                    // this.shipmentService.getSupplierName(data.WarpSupplierCode).then(x => {
                    //     supplierName = x.SupplierName;
                    //     supplierSiteCode = x.SerializationCode;
                    //     me.store(me.user_SupplierName, supplierName);
                    //     me.store(me.user_SupplierSiteCode, supplierSiteCode);
                    //     me._sessionSettings.reloadSettings();
                    // });
                    // this.shipmentService.getMultipleSupplierName(listOfSupplierCodes).then(x => {
                    //     me.store(me.user_SuppliersData, x);
                    //     let tempData :UserSuppliersData = x.filter(x => x.SupplierCode === data.WarpSupplierCode);
                    //     if(tempData!= null){
                    //         supplierName = tempData[0].Name;
                    //         supplierSiteCode = tempData[0].SerializationCode;
                    //         me.store(me.user_SupplierName, tempData[0].Name);
                    //         me.store(me.user_SupplierSiteCode, tempData[0].SerializationCode);

                    //     }
                    //     me._sessionSettings.reloadSettings();
                    // });
                }

                Cookie.set(
                    ret.cookieName,
                    ret.cookieToken,
                    365,
                    '/',
                    null,
                    true
                );

                me._router.navigate(['/']);
            })
            .catch(error => {
                me.ResetAuthorizationData();

                me.store(me.auth_validate_error, 'unhandled_exception');
                me.store(
                    me.auth_validate_errordesc,
                    'There was a problem during the authentication. Please try again or contact Tesla Support.'
                );

                me._router.navigate(['/auth/unauthorized']);

                return Promise.reject(error.message || error);
            });
    }

    /**
     * It Cancels the session on the Identity Server and across all SSO sessions.
     * It basically redirects to the Identity Server/Logout url.
     */

    public Logout() {

        const oktaSignIn = new OktaSignIn({
            baseUrl: this.appSettings.OpenId_BaseUrl,
            clientId: this.appSettings.OpenId_ClientID
        });

        // TODO: Take out this self. We should not need it with typescript.
        const logoutAndRedirect = () => {
            this.ResetAuthorizationData();
            window.localStorage.clear();

            // Redirect to Login Url.
           // if(!!self._configuration.Env || self._configuration.Env == "PRD" || self._configuration.Env == "production") {
                window.location.href = this.appSettings.SupplierPortalSignOutUrl;
                this._router.navigate(['/']);
           // } else {
           //     self.RequestAuthentication();
           // }
        };

        // if session exists.
        oktaSignIn.session.exists(function (exists: boolean) {
            if (exists) {
                oktaSignIn.signOut(function() {
                    logoutAndRedirect();
                });
            } else {
                logoutAndRedirect();
            }
        });

    }
    public Logout_temp() {
        const oktaSignIn = new OktaSignIn({
            baseUrl: this.appSettings.OpenId_BaseUrl,
            clientId: this.appSettings.OpenId_ClientID,
            revokeUrl: this.appSettings.OpenId_RevokeUrl,
        });

        // var signIn = new OktaSignIn(
        //     {
        //         baseUrl: environment.OpenId_BaseUrl
        //     }
        //   );

        //   signIn.session.close(function (err) {
        //     if (err) {
        //       // The user has not been logged out, perform some error handling here.
        //       return;
        //     }
        //     // The user is now logged out. Render the Sign-In Widget.
        //     this.ResetAuthorizationData();
        //     window.localStorage.clear();
        //     window.close();
        //   });

        // TODO: Take out this self. We should not need it with typescript.
        // var logoutAndRedirect = () => {
        //     this.ResetAuthorizationData();
        //     window.localStorage.clear();
        //     let revokeUrl = environment.OpenId_RevokeUrl;
        //     let client_id = environment.OpenId_ClientID;

        //     let url =
        //     revokeUrl +
        //         '?' +
        //         'client_id=' +
        //         encodeURI(client_id);

        //     //window.location.href = url;
        //     // this._apiHttpClient.post(url,'');
        //     // Redirect to Login Url.
        //     // if(!!self._configuration.Env || self._configuration.Env == "PRD" || self._configuration.Env == "production") {
        //     // window.location.href = environment.SupplierPortalSignOutUrl;
        //     //window.location.href = environment.OpenId_RevokeUrl;
        //     // } else {
        //     //     self.RequestAuthentication();
        //     // }
        // };

        // // if session exists.
        // oktaSignIn.session.exists(function(exists: boolean) {
        //     if (exists) {
        //         oktaSignIn.signOut(function() {
        //             logoutAndRedirect();
        //         });
        //     } else {
        //         logoutAndRedirect();
        //     }
        // });
    }

    /**
     * Determines if there is a valid token and that it is not yet expired.
     *
     * @return {boolean}
     */
    public IsAuthenticated(): boolean {
        const token = this.retrieve(this.access_token);
        const isTokenExpired = this.isTokenExpired(token);

        return token != null && !this.isTokenExpired(token); // !!this.retrieve(this.token_name);
    }

    /**
     * Goes through the claims and determines if the user has the requested permission.
     */
    public HasPermission(role: UserPermissions): boolean {
        const permissions = this.GetPermissions();
        return permissions.includes(role);
    }

    public GetPermissions(): UserPermissions[] {
        const permissions = this.retrieve(this.user_Permissions);
        if (permissions != null) {
            const result: Array<UserPermissions> = [];

            Object.keys(permissions).forEach(value => {
                const permission = UserPermissions[value];
                if (permission != null && permission !== undefined) {
                    result.push(permission);
                }
            });
            return result;
        }
        return [];
    }

    private ResetAuthorizationData() {
        this.deleteFromStorage(this.access_token);
        this.deleteFromStorage(this.user_email);
        this.deleteFromStorage(this.user_displayname);
        this.deleteFromStorage(this.user_username);
        this.deleteFromStorage(this.user_currentSupplierCode);
        this.deleteFromStorage(this.user_typeid);
        this.deleteFromStorage(this.user_CSRFToken);
        this.deleteFromStorage(this.user_SupplierName);
        this.deleteFromStorage(this.user_SupplierSiteCode);
        this.deleteFromStorage(this.user_SubcontractSuppliersLookup);
        this.deleteFromStorage(this.user_IsSupplierEdiEnabled);
        this.deleteFromStorage(this.user_SuppliersData);
        this.deleteFromStorage(this.user_isPlanner);
        this.deleteFromStorage(this.user_isEmployee);
        this.deleteFromStorage(this.user_isSupplier);
        this.deleteFromStorage(this.user_Permissions);
    }

    public getTokenExpirationDate(token: string): Date {
        let decoded: any;
        decoded = this.parseDataFromToken(token);

        if (!decoded.hasOwnProperty('exp')) {
            return null;
        }

        const date = new Date(0); // The 0 here is the key, which sets the date to the epoch
        date.setUTCSeconds(decoded.exp);

        return date;
    }

    public isTokenExpired(token: string, offsetSeconds?: number): boolean {
        const date = this.getTokenExpirationDate(token);
        offsetSeconds = offsetSeconds || 0;

        if (date == null) {
            return false;
        }

        // Token expired?
        return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
    }

    /**
     * Transforms the token string into an object.
     * TODO: Return a more representative object.
     *
     * @param {string} token -
     * @return {any} Object
     */
    private parseDataFromToken(token: string): any {
        let data = {};
        if (typeof token !== 'undefined') {
            const encoded = token.split('.')[1];
            data = JSON.parse(this.urlBase64Decode(encoded));
        }

        return data;
    }

    /**
     * Retrieves a key's value from the underlying storage.
     *
     * @param {string} key - Key under which the value is stored.
     * @return {any} - The object value.
     */
    public retrieve(key: string): any {
        const item = this.storage.getItem(key);

        if (item && item !== 'undefined') {
            return JSON.parse(this.storage.getItem(key));
        }

        return;
    }

    /**
     * Stores a key value pair into the underlying storage.
     *
     * @param {string} key - Key to store
     * @param {any} - Value to store
     */
    public store(key: string, value: any) {
        this.storage.setItem(key, JSON.stringify(value));
    }

    /**
     * Deletes a key from storage
     *
     * @param {string} key - Key to store
     */
    private deleteFromStorage(key: string) {
        this.storage.removeItem(key);
    }

    /**
     * Decodes a base 64 url into a string.
     *
     * @param {string} str - String to decode
     * @return {string}
     */
    private urlBase64Decode(str: string): string {
        let output = str.replace('-', '+').replace('_', '/');
        switch (output.length % 4) {
            case 0:
                break;
            case 2:
                output += '==';
                break;
            case 3:
                output += '=';
                break;
            default:
                throw new Error('Illegal base64url string!');
        }

        return window.atob(output);
    }
}
